import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, LayoutTemplate, Pencil, Users, AlertTriangle } from "lucide-react";
import { v2ListComponents } from "../../SalaryApiV2";
import { normalizeApiError } from "../../../../../utils/normalizeApiError";
import { listEmployeeSalaryStructures } from "../../../SalaryApi";
import PipelineCtcSection from "./PipelineCtcSection";
import PipelineReviewEmployeeAllocation from "./PipelineReviewEmployeeAllocation";
import { formatInr } from "./pipelineCtcUtils";
import API from "../../../../../services/api";
import { submitSalaryTemplatePipeline } from "./submitSalaryTemplatePipeline";
import { listCatalogComponents } from "./pipelineComponentFilter";
import { useToast } from "../../../../Context/ToastContext";
import { formulaOf, mappingText, isEarn } from "../../wizard/shared";
import StructureGrossPanel from "./StructureGrossPanel";
import { readableFormula } from "./buildGrossAllocation";
import SalaryTemplatePipelineBar from "./SalaryTemplatePipelineBar";
import {
  PIPELINE_STEPS,
  pipelineStepPath,
  readPipelineState,
  validatePipelineForSubmit,
  writePipelineState,
  clearPipelineState,
} from "./salaryTemplatePipeline";

export default function SalaryTemplatePipelineReview() {
  const toast = useToast();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(() => readPipelineState());
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [savedAsEdit, setSavedAsEdit] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameBuf, setNameBuf] = useState(pipeline.templateName || "");
  const [assignedRows, setAssignedRows] = useState([]);
  const [employeeBalance, setEmployeeBalance] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const state = readPipelineState();
        setPipeline(state);
        setNameBuf(state.templateName || "");
        const [allComps, cfgRes] = await Promise.all([
          v2ListComponents(),
          API.get("/organisation/me/extra-work-config").catch(() => null),
        ]);
        const extra = cfgRes?.data?.extra_work || null;
        if (!cancelled) {
          setComps(listCatalogComponents(allComps, extra));
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to load review data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const state = readPipelineState();
      const drafts = state.draftEmployeeAssignments || [];
      // Once the user edits assignments, drafts are the source of truth (supports custom packages).
      if (drafts.length) {
        if (!cancelled) setAssignedRows(drafts);
        return;
      }
      if (state.templateId) {
        try {
          const rows = await listEmployeeSalaryStructures();
          const filtered = (Array.isArray(rows) ? rows : [])
            .filter((r) => String(r.template_id) === String(state.templateId))
            .map((r) => ({
              employee_id: r.employee_id,
              employee_name: r.employee_name,
              employee_code: r.employee_code,
              department: r.department,
              designation: r.designation,
              ctc: r.ctc != null && r.ctc !== "" ? Number(r.ctc) : null,
              template_name: r.template_name,
              effective_from: r.effective_from,
            }));
          if (!cancelled) setAssignedRows(filtered);
        } catch {
          if (!cancelled) setAssignedRows([]);
        }
        return;
      }
      if (!cancelled) setAssignedRows([]);
    })();
    return () => {
      cancelled = true;
    };
  }, [pipeline.templateId, pipeline.draftEmployeeAssignments]);

  useEffect(() => {
    const target = Number(pipeline.targetAnnualCtc);
    const next = {};
    for (const row of assignedRows) {
      const key = String(row.employee_id || "");
      if (!key) continue;
      const ctc = Number(String(row.ctc ?? "").replace(/,/g, ""));
      // Custom packages are allowed — only require a positive annual gross.
      if (!Number.isFinite(ctc) || ctc <= 0) {
        next[key] = "missing";
      } else if (Number.isFinite(target) && target > 0 && Math.abs(ctc - target) <= 1) {
        next[key] = "balanced";
      } else {
        next[key] = "custom";
      }
    }
    setEmployeeBalance(next);
  }, [assignedRows, pipeline.targetAnnualCtc]);

  function handleDraftAssignmentsChange(nextDraft) {
    setAssignedRows(nextDraft);
    setPipeline(readPipelineState());
  }

  const allocated = useMemo(() => {
    const ids = new Set((pipeline.allocatedComponentIds || []).map(String));
    return (comps || []).filter((c) => ids.has(String(c.component_id)));
  }, [comps, pipeline.allocatedComponentIds]);

  const earnings = useMemo(() => allocated.filter(isEarn), [allocated]);
  const deductions = useMemo(() => allocated.filter((c) => !isEarn(c)), [allocated]);

  function goEditComponents() {
    writePipelineState({ returnToReview: true, currentStep: PIPELINE_STEPS.components });
    navigate(pipelineStepPath(PIPELINE_STEPS.components, readPipelineState()));
  }

  function goEditFormulas() {
    writePipelineState({ returnToReview: true, currentStep: PIPELINE_STEPS.formulas });
    navigate(pipelineStepPath(PIPELINE_STEPS.formulas));
  }

  function removeComponent(id) {
    const ids = (pipeline.allocatedComponentIds || []).map(String).filter((x) => x !== String(id));
    const next = writePipelineState({ allocatedComponentIds: ids });
    setPipeline(next);
    toast.success("Component removed from this structure.");
  }

  function goEditCtc() {
    writePipelineState({ returnToReview: true, currentStep: PIPELINE_STEPS.ctcAllocation });
    navigate(pipelineStepPath(PIPELINE_STEPS.ctcAllocation));
  }

  const validationSummary = useMemo(() => {
    const state = pipeline;
    const empCount = assignedRows.length;
    const balanced = Object.values(employeeBalance).filter((s) => s === "balanced").length;
    const custom = Object.values(employeeBalance).filter((s) => s === "custom").length;
    const ready = Object.values(employeeBalance).filter((s) => s === "balanced" || s === "custom").length;
    const needsAdj = Object.values(employeeBalance).filter((s) => s === "missing").length;
    const hasTarget = Number(state.targetAnnualCtc) > 0;
    return {
      empCount,
      balanced,
      custom,
      ready,
      needsAdj,
      hasTarget,
      components: (state.allocatedComponentIds || []).length,
    };
  }, [pipeline, assignedRows, employeeBalance]);

  async function saveTemplateName() {
    const trimmed = nameBuf.trim();
    if (!trimmed) {
      toast.error("Structure name cannot be empty.");
      return;
    }
    try {
      const next = writePipelineState({ templateName: trimmed });
      setPipeline(next);
      setEditName(false);
      toast.success("Structure name updated.");
    } catch (e) {
      toast.error(e?.detail || e?.message || "Could not save name");
    }
  }

  async function handleCreateTemplate() {
    const known = new Set((comps || []).map((c) => String(c.component_id)));
    const currentIds = [...new Set((readPipelineState().allocatedComponentIds || []).map(String))];
    const kept = comps.length ? currentIds.filter((id) => known.has(id)) : currentIds;
    writePipelineState({
      draftEmployeeAssignments: assignedRows,
      allocatedComponentIds: kept,
    });
    const state = readPipelineState();
    const validation = validatePipelineForSubmit(state, comps);
    if (!validation.ok) {
      toast.error(validation.errors[0]);
      return;
    }
    setSubmitting(true);
    try {
      const saved = await submitSalaryTemplatePipeline(state, comps);
      const updated = Boolean(saved?.updated || state.editMode || state.templateId);
      clearPipelineState();
      toast.success(updated ? "Salary structure updated." : "Salary structure saved.");
      navigate("/salary-v2/structures", { replace: true });
    } catch (e) {
      toast.error(normalizeApiError(e) || "Could not save this salary structure. Check the structure name and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <section className="stp-card stp-review-success">
        <CheckCircle2 size={48} color="#059669" />
        <h2>{savedAsEdit ? "Salary structure updated" : "Salary structure saved"}</h2>
        <p className="stp-section-hint">
          {pipeline.templateName || "Structure"} is ready for payroll.
        </p>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap", marginTop: "1rem" }}>
          <button type="button" className="stp-btn stp-btn-primary" onClick={() => navigate("/salary-v2/structures")}>
            Back to salary structures
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <SalaryTemplatePipelineBar currentStep={PIPELINE_STEPS.review} />

      <section className="stp-card">
        <div className="stp-list-head">
          <div>
            <h2 className="stp-section-title">
              <LayoutTemplate size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Review salary structure
            </h2>
            <p className="stp-section-hint">
              Same review for a new structure and an existing one. Change anything below, then save.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="stp-section-hint">Loading review…</p>
        ) : (
          <>
            <div className="stp-review-section">
              <div className="stp-review-section-head">
                <h3 className="stp-review-section-title">Structure</h3>
                {!editName ? (
                  <button type="button" className="stp-btn stp-btn-ghost" onClick={() => setEditName(true)}>
                    <Pencil size={14} /> Edit name
                  </button>
                ) : null}
              </div>
              {!editName ? (
                <p>
                  <strong>Salary structure:</strong> {pipeline.templateName || "—"}
                </p>
              ) : (
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    className="stp-input"
                    value={nameBuf}
                    onChange={(e) => setNameBuf(e.target.value)}
                    aria-label="Template name"
                  />
                  <button type="button" className="stp-btn stp-btn-primary" onClick={saveTemplateName}>
                    Save
                  </button>
                  <button type="button" className="stp-btn stp-btn-ghost" onClick={() => setEditName(false)}>
                    Cancel
                  </button>
                </div>
              )}
              {pipeline.templateDescription ? (
                <p className="stp-section-hint">{pipeline.templateDescription}</p>
              ) : null}
            </div>

            <div className="stp-review-section">
              <div className="stp-review-section-head">
                <h3 className="stp-review-section-title">Components and formulas</h3>
                <button type="button" className="stp-btn stp-btn-ghost" onClick={goEditComponents}>
                  <Pencil size={14} /> Add or edit components
                </button>
              </div>
              <table className="stp-review-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Type</th>
                    <th>Formula</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {[...earnings, ...deductions].map((c) => {
                    const id = String(c.component_id);
                    const fx = pipeline.formulas?.[id]?.expression ?? formulaOf(c);
                    return (
                      <tr key={id}>
                        <td>{c.name}</td>
                        <td>{isEarn(c) ? "Earning" : "Deduction"}</td>
                        <td><code>{readableFormula(fx) || mappingText(c)}</code></td>
                        <td>
                          <button type="button" className="stp-btn stp-btn-ghost" onClick={goEditFormulas}>Edit</button>
                          <button type="button" className="stp-btn stp-btn-ghost" onClick={() => removeComponent(id)}>Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                  {!allocated.length ? (
                    <tr><td colSpan={4}>No components selected.</td></tr>
                  ) : null}
                </tbody>
              </table>
              <StructureGrossPanel
                components={allocated}
                formulas={pipeline.formulas}
                monthlyGross={
                  Number(pipeline.targetAnnualCtc) > 0
                    ? String(Math.round(Number(pipeline.targetAnnualCtc) / 12))
                    : ""
                }
                onMonthlyGrossChange={(value) => {
                  const monthly = Number(String(value || "").replace(/,/g, ""));
                  const next = writePipelineState({
                    targetAnnualCtc: monthly > 0 ? Math.round(monthly * 12) : null,
                  });
                  setPipeline(next);
                }}
              />
            </div>

            <div className="stp-review-section">
              <div className="stp-review-section-head">
                <h3 className="stp-review-section-title">Gross salary</h3>
                <button type="button" className="stp-btn stp-btn-ghost" onClick={goEditCtc}>
                  <Pencil size={14} /> Edit gross salary
                </button>
              </div>
              {validationSummary.hasTarget ? (
                <p className="stp-section-hint">
                  Target package: {formatInr(pipeline.targetAnnualCtc)} / year (
                  {formatInr(Number(pipeline.targetAnnualCtc) / 12)} / month)
                </p>
              ) : (
                <p className="stp-section-hint">No gross salary set. You can still save the structure.</p>
              )}
              <PipelineCtcSection
                compact
                templateId={pipeline.templateId || null}
                componentIds={pipeline.allocatedComponentIds || []}
              />
            </div>

            <div className="stp-review-section">
              <div className="stp-review-section-head">
                <h3 className="stp-review-section-title">
                  <Users size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  Employee allocation
                </h3>
              </div>
              <PipelineReviewEmployeeAllocation
                templateName={pipeline.templateName}
                targetAnnualCtc={pipeline.targetAnnualCtc}
                assignedRows={assignedRows}
                employeeBalance={employeeBalance}
                onAssignmentsChange={handleDraftAssignmentsChange}
              />
              {assignedRows.length ? (
                <p className="stp-section-hint" style={{ marginTop: "0.75rem" }}>
                  {validationSummary.ready} of {validationSummary.empCount} packages ready
                  {validationSummary.custom
                    ? ` · ${validationSummary.custom} custom package${validationSummary.custom === 1 ? "" : "s"}`
                    : ""}
                  {validationSummary.needsAdj
                    ? ` · ${validationSummary.needsAdj} still need an annual salary`
                    : ""}
                </p>
              ) : null}
            </div>

            <div className="stp-review-section stp-review-validation">
              <h3 className="stp-review-section-title">Final validation</h3>
              <ul className="stp-validation-list">
                <li>
                  <CheckCircle2 size={14} /> Components: {validationSummary.components}
                </li>
                <li>
                  <CheckCircle2 size={14} /> Employees: {validationSummary.empCount}
                </li>
                <li>
                  {validationSummary.needsAdj ? (
                    <AlertTriangle size={14} />
                  ) : (
                    <CheckCircle2 size={14} />
                  )}
                  Gross salary:{" "}
                  {validationSummary.empCount
                    ? `${validationSummary.ready} / ${validationSummary.empCount} ready${
                        validationSummary.custom
                          ? ` (${validationSummary.custom} custom)`
                          : ""
                      }`
                    : validationSummary.hasTarget
                      ? "Structure target configured"
                      : "Not configured"}
                </li>
                <li>
                  <CheckCircle2 size={14} /> Formula validation: draft ready
                </li>
                <li>
                  <CheckCircle2 size={14} /> Payroll calculation: engine ready
                </li>
              </ul>
            </div>

          </>
        )}

        <div className="stp-pipeline-sticky">
          <button
            type="button"
            className="stp-btn stp-btn-ghost"
            onClick={() => navigate(pipelineStepPath(PIPELINE_STEPS.ctcAllocation))}
          >
            Back
          </button>
          <button
            type="button"
            className="stp-btn stp-btn-primary"
            disabled={submitting || loading}
            onClick={handleCreateTemplate}
          >
            {submitting
              ? pipeline.editMode
                ? "Saving…"
                : "Creating…"
              : pipeline.editMode
                ? "Save salary structure"
                : "Save salary structure"}
            {!submitting ? <ArrowRight size={16} /> : null}
          </button>
        </div>
      </section>
    </>
  );
}
